import { useState } from "react";
import { Link } from "react-router-dom";
import { Ban, Printer } from "lucide-react";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg } from "@/lib/api";
import { Empty, ErrorBox, ExportBar, Loading, PageHeader, PermitBadge, ReasonDialog, Sel } from "@/components/common";

export default function Permits() {
  const { can } = useAuth();
  const [f, setF] = useState({ q: "", status: "", date_from: "", date_to: "" });
  const [applied, setApplied] = useState(f);
  const [cancelId, setCancelId] = useState(null);
  const params = Object.fromEntries(Object.entries(applied).filter(([, v]) => v));
  const { data, error, loading, reload } = usePoll("/permits", params, 20000);
  const rows = data || [];
  const set = (k) => (v) => { const n = { ...f, [k]: v }; setF(n); setApplied(n); };
  return (
    <div>
      <PageHeader title="تصاريح الخروج" subtitle="لإصدار تصريح جديد افتح عملية البيع من «المبيعات والأرصدة» وتحقق من الشروط">
        <ExportBar name="permits" rows={rows} cols={[{ key: "number", label: "رقم التصريح" }, { key: "sale_number", label: "رقم البيع" }, { key: "ear_tag", label: "رقم الكبش" },
          { key: "customer_name", label: "الزبون" }, { key: "status", label: "الحالة" }, { label: "الإصدار", get: (r) => dt(r.issued_at) }, { key: "issued_by", label: "أصدره" },
          { label: "التسليم", get: (r) => dt(r.delivered_at) }, { label: "الخروج", get: (r) => dt(r.exited_at) }, { key: "exited_by", label: "الحارس" }]} />
        <Link className="k-btn-accent" to="/sales?delivery_status=none" data-testid="permits-go-sales">اختيار عملية بيع</Link>
      </PageHeader>
      <div className="k-card p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 no-print">
        <form className="col-span-2" onSubmit={(e) => { e.preventDefault(); setApplied(f); }}>
          <input className="k-input" placeholder="رقم التصريح، البيع، الكبش، الزبون… ثم Enter" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="permits-search-input" />
        </form>
        <Sel value={f.status} onChange={set("status")} testid="permits-filter-status"><option value="">كل الحالات</option><option value="valid">صالح</option><option value="delivered">صالح – سُلّم</option><option value="used">مستخدم – خرج</option><option value="cancelled">ملغى</option></Sel>
        <input type="date" className="k-input" value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} />
        <input type="date" className="k-input" value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} />
      </div>
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : (
        <div className="k-card overflow-x-auto">
          <table className="k-table" data-testid="permits-table">
            <thead><tr><th>رقم التصريح</th><th>البيع</th><th>الكبش</th><th>الزبون</th><th>الحالة</th><th>الإصدار</th><th>التسليم</th><th>الخروج</th><th></th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} data-testid={`permit-row-${p.number}`}>
                <td className="font-bold num">{p.number}{p.exception && <span className="block text-xs text-rose-700">باستثناء</span>}</td>
                <td><Link className="underline" to={`/sales/${p.sale_id}`}>{p.sale_number}</Link></td><td className="num text-lg">{p.ear_tag}</td><td>{p.customer_name}</td>
                <td><PermitBadge p={p} /></td><td className="text-xs">{dt(p.issued_at)}<br />{p.issued_by}</td>
                <td className="text-xs">{p.delivered_at ? <>{dt(p.delivered_at)}<br />{p.delivered_by}</> : "—"}</td>
                <td className="text-xs">{p.exited_at ? <>{dt(p.exited_at)}<br />{p.exited_by}</> : "—"}</td>
                <td className="whitespace-nowrap">
                  <Link className="k-btn-outline k-btn-sm" to={`/print/permit/${p.id}`} target="_blank"><Printer className="w-4 h-4" /></Link>
                  {p.status === "valid" && can("cancel_permit") && <button className="k-btn-outline k-btn-sm text-rose-700 mr-1" onClick={() => setCancelId(p.id)} data-testid={`permits-cancel-${p.number}`}><Ban className="w-4 h-4" /></button>}
                </td>
              </tr>))}</tbody>
          </table>
          {!rows.length && <Empty text="لا توجد تصاريح" />}
        </div>
      )}
      <ReasonDialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)} title="إلغاء تصريح الخروج" testid="permits-cancel-dialog"
        onConfirm={async (reason) => { try { await api.post(`/permits/${cancelId}/cancel`, { reason }); toast.success("تم إلغاء التصريح"); reload(); } catch (e) { throw new Error(errMsg(e)); } }} />
    </div>
  );
}
